import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import _ from 'lodash';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { t, translateDate } from '../common/Translate';
import FormRegVin from './FormRegVin';
import FormVehicle from './FormVehicle';
import Block from '../common/Display/Block';
import useSource from '../../hooks/useCalendar/useSource';
import { recordManager, Auth } from '../../config/configuration/react';
import { RECORD_STATUS, REACT_APP_API, ER_GROUP_ID } from '../../config';
import { DefaultAlertModal } from '../common/Modal/DefaultAlertModal';
import MeetingTag from '../Calendar/MeetingTag';
import { formatVehicle } from '../../Utils/presenters/vehicle/vehiclePresenter';
import { Record } from '../../config/Record/RecordEntity';
import getVehicleByVin from '../../services/SearchVehicle/getVehicleByVin';

const {
  expertise_finished,
  expertise_authorized,
  supervisor_refused,
  refused,
  report,
  repurchase_account,
  waiting_for_counter_appraisal,
  counter_appraisal_ongoing,
  counter_appraisal_verification,
} = RECORD_STATUS;

const VehicleSearch = (props) => {
  const { recordId } = useParams();
  const record = useSelector((state) => state.reduxRecords.data[recordId]);
  const originId = useSelector((state) => state?.reduxRecords?.data[recordId]?.record?.originId);
  const recordEntity = new Record(record);
  const [identificationMode, setIdentificationMode] = useState(
    (recordEntity.status() === 18 && 'registration') || 'vehicle_informations',
  ); // registration
  const [vehicle, setVehicle] = useState((record && formatVehicle(record)) || {});
  const language = useSelector((state) => state?.reduxRecords?.data[recordId]?.record?.language);
  const [registration, setRegistration] = useState('');
  const [image, setImage] = useState(null);
  const isVinDecode = useRef('');
  const [versions, setVersions] = useState([]);
  const [updateInfoLoading, setUpdateInfoLoading] = useState(false);
  const [blockDecoding, setBlockDecoding] = useState(false);
  const [manuelSearch, setManuelSearch] = useState({});
  const [manuelSearchVinDecoding, setManuelSearchVinDecoding] = useState({});
  const [errors, setErrors] = useState({});
  const [selectMode, setSelectMode] = useState(null);
  const { source } = useSource();
  const [alertModal, setAlertModal] = useState(false);
  const [proceedFormCreation, setProceedFormCreation] = useState(false);
  const [usedImmat, setUsedImmat] = useState(false);
  const [newGrayCard, setNewGrayCard] = useState(false);
  const [grayCardMsg, setGrayCardMsg] = useState('');
  const [usedImmatRecords, setUsedImmatRecords] = useState(null);
  const [alertModalBody, setAlertModalBody] = useState('');
  const [preservedVehicle, setPreservedVehicle] = useState(null);
  const [histovecInformation, setHistovecInformation] = useState({});
  const entryStockOption = useSelector((state) => state.reduxRecords.data[recordId])?.entryStockOption;
  let supervisorEntryStockDate = useSelector(
    (state) => state.reduxRecords.data[recordId]?.supervisor?.entryStockDate?.date,
  );
  supervisorEntryStockDate = supervisorEntryStockDate && moment(supervisorEntryStockDate).format('YYYY/MM/DD');
  const supervisorEntryStockKey = useSelector((state) => state.reduxRecords.data[recordId])?.supervisor
    ?.entryStockValue;
  const [entryStockValue, setEntryStockValue] = useState(supervisorEntryStockKey ?? 'select');
  const [entryStockDate, setEntryStockDate] = useState(supervisorEntryStockDate ?? 'select');
  const country = Auth.country()?.toLowerCase();
  const [pictureSuccess, setPictureSuccess] = useState(false);
  const getEntryStockDate = useCallback(
    (v) => {
      if (v === 'select') {
        setEntryStockValue('select');
        return setEntryStockDate('select');
      }
      if (entryStockOption) {
        entryStockOption.map((i) => {
          if (_.get(i, parseInt(v, 10))) {
            setEntryStockValue(parseInt(v, 10));
            return setEntryStockDate(_.get(i, parseInt(v, 10)));
          }
          return null;
        });
      }
      return null;
    },
    [entryStockValue, entryStockOption],
  );

  const toggleAlert = () => setAlertModal(!alertModal);
  const validateAlert = () => setProceedFormCreation(true);
  const cancelAlert = () => {
    setAlertModal(false);
    setProceedFormCreation(false);
    setIdentificationMode('registration');
  };
  const role = Auth.role();
  const fileName = country === 'es' ? 'informe_estadoVO' : 'rapport_d_inspection';

  const setVehValue = (veh) => {
    setRegistration('');
    const _veh = veh;
    _veh.carTypeId = _veh?.vehicleType?.id;
    _veh.carTypeLabel = _veh?.vehicleType?.name;
    delete _veh?.vehicleType;
    setVehicle(_veh);
    if (identificationMode === 'registration') setIdentificationMode('vehicle_informations');
  };

  const checkUsedImmat = async () => {
    let check = false;
    try {
      // check for already used immat
      const query = {
        registration: vehicle.registration,
        status: [
          expertise_finished,
          expertise_authorized,
          supervisor_refused,
          refused,
          report,
          repurchase_account,
          ...(originId < 10
            ? [waiting_for_counter_appraisal, counter_appraisal_ongoing, counter_appraisal_verification]
            : []),
        ],
        range: [0, 9],
        sourceIds: Auth.sourceIds().join(','),
        verifAccess: 'false',
        recordTypeId: 1,
        groupId: Auth.groupId(),
        mode: 'appraisal',
      };

      const { records } = await recordManager.search(query);
      if (_.isEmpty(records)) {
        throw new Error('empty datas');
      } else {
        check = true;
        setUsedImmatRecords(records);
        setUsedImmat(check);
      }
    } catch (error) {
      setUsedImmat(check);
    }
    return check;
  };

  const checkGCCreation = () => {
    let check = false;
    const sixMonthDateFromNow = moment().subtract(6, 'months').format('YYYY-MM-DD');
    const fiveMonthDateFromNow = moment().subtract(5, 'months').format('YYYY-MM-DD');
    if (
      recordEntity.getGroupId() === ER_GROUP_ID &&
      moment(vehicle.dateGrayCard).isSameOrAfter(fiveMonthDateFromNow) === true
    ) {
      check = true;
      setNewGrayCard(check);
      setGrayCardMsg('popUpRegistrationDocLessFiveMonthsMessage');
    } else if (
      recordEntity.getGroupId() !== ER_GROUP_ID &&
      moment(vehicle.dateGrayCard).isSameOrAfter(sixMonthDateFromNow) === true
    ) {
      check = true;
      setNewGrayCard(check);
      setGrayCardMsg('popUpRegistrationDocLessSixMonthsMessage');
    } else {
      setNewGrayCard(check);
    }
    return check;
  };

  const checkImmatGCProcess = async () => {
    const checkUsedRegis = await checkUsedImmat();
    if (checkGCCreation() === true || checkUsedRegis === true) {
      setAlertModal(true);
      setProceedFormCreation(false);
    } else {
      setAlertModal(false);
      setProceedFormCreation(true);
    }
  };

  useEffect(() => {
    if (usedImmat || newGrayCard) {
      let message;
      if (newGrayCard && !usedImmat) message = grayCardMsg;
      else if (newGrayCard && usedImmat) message = 'popUpLicensePlateRecognized2Combined';
      else message = 'popUpLicensePlateRecognized2';

      const modalBody = () => (
        <>
          <p className="modal-p">{message && t(message)}</p>

          {usedImmat && (
            <div className="overflow-auto mt-2 mb-2">
              <table className="modal-table">
                <thead className="modal-table-thead">
                  <tr>
                    <td>{t('file_number')}</td>
                    <td>{t('creation_date')}</td>
                    <td>{t('status')}</td>
                    <td>{t('pointOfSaleName')}</td>
                    <td>{t('buyer')}</td>
                    {recordEntity.type() === 1 && <td>{t('totalRefurbishmentCosts')}</td>}
                    <td>{t('offer')}</td>
                    {recordEntity.type() === 2 && <td>{t('appraisalLinked')}</td>}
                    <td>{t('pdf')}</td>
                  </tr>
                </thead>
                <tbody>
                  {_.map(usedImmatRecords, (r) => (
                    <tr key={r.recordId} className="modal-table-tr">
                      <td className="modal-table-td">
                        {['manager', 'admin', 'superAdmin', 'multiSupervisor'].includes(role) ||
                        (['buyer', 'supervisor'].includes(role) &&
                          Auth.concessionIds().includes(r.concessionCarcheckId)) ? (
                          <Link to={`/files/${r.recordId}`} target="_blank">
                            <u>{r.refHexaId}</u>
                          </Link>
                        ) : (
                          r.refHexaId
                        )}
                      </td>
                      <td className="modal-table-td">{translateDate(r.createdAt.date, 'DD-MM-YYYY')}</td>
                      <td className="modal-table-td">
                        <MeetingTag code={r.statusCarcheck} />
                      </td>
                      <td className="modal-table-td">{r.concessionName}</td>
                      <td className="modal-table-td">{r.buyerFullName}</td>
                      {recordEntity.type() === 1 && <td className="modal-table-td">{_.round(r.repairCosts)}</td>}
                      <td className="modal-table-td">{_.round(r.c2bOffer)}</td>
                      {recordEntity.type() === 2 && (
                        <td className="modal-table-td">
                          <input
                            id="appraisalLinked"
                            name="appraisalLinked"
                            checked={vehicle?.appraisalLinkedRecord === r.recordId}
                            onChange={(e) => {
                              if (e.target.checked) vehicle.appraisalLinkedRecord = r.recordId;
                              else delete vehicle?.appraisalLinkedRecord;
                              setVehicle({ ...vehicle });
                            }}
                            type="checkbox"
                          />
                        </td>
                      )}
                      <td className="modal-table-td">
                        {['manager', 'admin', 'superAdmin', 'multiSupervisor'].includes(role) ||
                        (['buyer', 'supervisor'].includes(role) &&
                          Auth.concessionIds().includes(r.concessionCarcheckId)) ? (
                          <a
                            target="_blank"
                            href={`${REACT_APP_API}/buyer/public/${r.sourceLabel}/${r.recordKey}/admin-documents/${r.recordId}_${fileName}.pdf`}
                            rel="noopener noreferrer"
                          >
                            <u>{t('seeReport')}</u>
                          </a>
                        ) : (
                          ''
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {newGrayCard && !usedImmat && <p className="modal-p">{t('popUpRegistrationDocLessSixMonthsMessageCont')}</p>}
          {newGrayCard && usedImmat && (
            <>
              <p className="modal-p">{t(grayCardMsg)}</p>
              <p className="modal-p">
                {recordEntity.type() === 1
                  ? (newGrayCard && t('popUpLicensePlateRecognizedTextContinue2Combined')) ||
                    t('popUpLicensePlateRecognizedTextContinue2')
                  : t('popUpLicensePlateRecognizedTextContinue3')}
              </p>
            </>
          )}
        </>
      );
      setAlertModalBody(modalBody);
    }
  }, [usedImmat, newGrayCard, alertModal, vehicle]);

  let alertCostumProps = {
    isOpen: alertModal,
    toggle: toggleAlert,
    body: alertModalBody,
    validateBtn: 'licensePlateRecognizedContinue',
    handleCancel: cancelAlert,
    handleValidate: validateAlert,
    centered: true,
    size: usedImmat ? 'lg' : 'md',
  };
  if (newGrayCard)
    alertCostumProps = {
      ...alertCostumProps,
      cancelBtn: 'cancel',
      validateBtn: 'licensePlateRecognizedContinueCombined',
    };

  const updateInfoWithVin = async () => {
    setUpdateInfoLoading(true);
    setManuelSearch({});
    setManuelSearchVinDecoding({ vin: vehicle?.vin, dateRelease: vehicle?.dateRelease });
    try {
      const [data] = await getVehicleByVin(
        vehicle?.vin,
        country,
        moment(vehicle?.dateRelease).format('YYYY-MM-DD'),
        language,
        'R',
      );
      if (data.doors) data.doorId = data.doors;
      if (data.engine) data.engineId = data.engine;
      if (data.gearboxId) data.gearId = data.gearboxId;
      if (data.gearboxName) data.gearName = data.gearboxName;
      if (data.vehicleType) {
        data.carTypeId = data?.vehicleType?.id;
        data.carTypeLabel = data?.vehicleType?.name;
      }
      await setVersions(data.versions || []);
      data.dateRelease = data.dateRelease ? moment(data?.dateRelease).toDate() : '';
      await setVersions(data?.versions || []);
      await setVehicle((perv) => ({ ...perv, ...data, versionType: 'vin' }));
      isVinDecode.current = 'true';
      setRegistration(data?.registration || '');
      await setIdentificationMode((perv) =>
        perv === 'updateInfoWithVin' ? 'vehicle_informations' : 'updateInfoWithVin',
      );
    } catch (err) {
      setErrors((perv) => ({ ...perv, updateWithVinError: 'VinCarDetailsNOtFound' }));
    } finally {
      setUpdateInfoLoading(false);
      setBlockDecoding(true);
    }
  };
  return (
    <>
      <Block
        {...props}
        noBack={recordEntity.status() !== 18}
        backRoute={_.set(props, 'location.state.from', `/files/${recordId}`)} // should go back to hub page!!
        showSubHeader
      >
        <Switcher
          identificationMode={identificationMode}
          setIdentificationMode={setIdentificationMode}
          showMenu={recordEntity.status() === 18}
        />

        {identificationMode === 'registration' && (
          <FormRegVin
            {...props}
            mode={source?.sourceGroupData?.carIdentification || ''}
            className={`d-md-block ${identificationMode === 'registration' ? '' : 'd-none'}`}
            setVehicle={setVehValue}
            setSelectMode={setSelectMode}
            entryStockValue={entryStockValue}
            entryStockDate={entryStockDate}
            getEntryStockDate={getEntryStockDate}
            setVersions={setVersions}
            setImage={setImage}
            image={image}
            histovecInformation={histovecInformation}
            setHistovecInformation={setHistovecInformation}
            setPictureSuccess={setPictureSuccess}
            pictureSuccess={pictureSuccess}
            setManuelSearchVinDecoding={setManuelSearchVinDecoding}
          />
        )}

        {identificationMode === 'vehicle_informations' && (
          <FormVehicle
            {...props}
            key="vehicle_informations"
            className={`d-md-block ${identificationMode === 'vehicle_informations' ? '' : 'd-none'}`}
            vehicle={vehicle}
            setVehicle={setVehicle}
            manuelSearch={manuelSearch}
            setManuelSearch={setManuelSearch}
            identificationMode={identificationMode}
            setIdentificationMode={setIdentificationMode}
            selectMode={selectMode}
            proceedFormCreation={proceedFormCreation}
            setProceedFormCreation={setProceedFormCreation}
            alertModal={alertModal}
            setAlertModal={setAlertModal}
            checkImmatGCProcess={checkImmatGCProcess}
            preservedVehicle={preservedVehicle}
            setPreservedVehicle={setPreservedVehicle}
            entryStockValue={entryStockValue}
            entryStockDate={entryStockDate}
            getEntryStockDate={getEntryStockDate}
            updateInfoWithVin={updateInfoWithVin}
            registration={registration}
            versions={versions}
            setVersions={setVersions}
            updateInfoLoading={updateInfoLoading}
            isVinDecode={isVinDecode}
            manuelSearchVinDecoding={manuelSearchVinDecoding}
            blockDecoding={blockDecoding}
            setBlockDecoding={setBlockDecoding}
            errors={errors}
            setErrors={setErrors}
          />
        )}
        {identificationMode === 'updateInfoWithVin' && (
          <FormVehicle
            {...props}
            key="updateInfoWithVin"
            className={`d-md-block ${identificationMode === 'updateInfoWithVin' ? '' : 'd-none'}`}
            vehicle={vehicle}
            setVehicle={setVehicle}
            manuelSearch={manuelSearch}
            setManuelSearch={setManuelSearch}
            identificationMode={identificationMode}
            setIdentificationMode={setIdentificationMode}
            selectMode={selectMode}
            proceedFormCreation={proceedFormCreation}
            setProceedFormCreation={setProceedFormCreation}
            alertModal={alertModal}
            setAlertModal={setAlertModal}
            checkImmatGCProcess={checkImmatGCProcess}
            preservedVehicle={preservedVehicle}
            setPreservedVehicle={setPreservedVehicle}
            entryStockValue={entryStockValue}
            entryStockDate={entryStockDate}
            getEntryStockDate={getEntryStockDate}
            updateInfoWithVin={updateInfoWithVin}
            registration={registration}
            versions={versions}
            setVersions={setVersions}
            updateInfoLoading={updateInfoLoading}
            isVinDecode={isVinDecode}
            manuelSearchVinDecoding={manuelSearchVinDecoding}
            blockDecoding={blockDecoding}
            setBlockDecoding={setBlockDecoding}
            errors={errors}
            setErrors={setErrors}
          />
        )}
      </Block>
      <DefaultAlertModal {...alertCostumProps} />
    </>
  );
};

const Switcher = ({ identificationMode, setIdentificationMode, showMenu }) => (
  <div className="menu-switcher">
    {showMenu && (
      <ul>
        <li className={identificationMode === 'registration' ? 'active' : ''}>
          <div role="button" tabIndex={0} onKeyPress={() => {}} onClick={() => setIdentificationMode('registration')}>
            {t('registration')}
          </div>
        </li>
        <li
          className={
            identificationMode === 'vehicle_informations' || identificationMode === 'updateInfoWithVin' ? 'active' : ''
          }
        >
          <div
            role="button"
            tabIndex={0}
            onKeyPress={() => {}}
            onClick={() => setIdentificationMode('vehicle_informations')}
          >
            {t('vehicle_informations')}
          </div>
        </li>
      </ul>
    )}
  </div>
);

Switcher.propTypes = {
  identificationMode: PropTypes.string.isRequired,
  setIdentificationMode: PropTypes.func.isRequired,
  showMenu: PropTypes.bool.isRequired,
};

export default VehicleSearch;
